// Plus purchase on the web: Razorpay subscription with UPI AutoPay.
// Live flow: POST /subscriptions/razorpay -> Razorpay Checkout opens the UPI app (phone) or shows
// a QR / UPI ID field (desktop) -> webhook confirms the mandate -> the app polls and shows "Done".
// In the preview the approval step is simulated and clearly labelled.
import { useEffect, useState, type CSSProperties } from 'react';
import { api, isLiveCheckout } from '../lib/api';
import { useCatalog } from '../lib/catalog';
import { date, rupees } from '../lib/format';
import { Link, back, navigate, useLocation, Redirect } from '../lib/router';
import { getState, hasPlus, setState, useApp } from '../lib/store';
import { useDesktop } from '../lib/useMedia';
import { Icon } from '../components/Icon';
import { Wordmark } from '../components/Logo';

const DAY = 86400_000;
const PHONE_APPS = [
  { name: 'PhonePe', short: 'Pe', color: '#3B2A6B' },
  { name: 'Google Pay', short: 'G', color: '#1F4E79' },
  { name: 'Paytm', short: 'P', color: '#0B5C6B' },
  { name: 'Other UPI app', short: '@', color: '#2E3135' },
];
const DESKTOP_WAYS = [
  { name: 'Scan a QR code', short: 'QR', color: '#3B2A6B', hint: 'Use any UPI app on your phone' },
  { name: 'Enter your UPI ID', short: '@', color: '#1F4E79', hint: 'Get a request in your UPI app' },
];

export function Paywall() {
  const { plan: PLAN, shows: SHOWS } = useCatalog();
  const session = useApp((s) => s.session);
  const plus = useApp((s) => hasPlus(s));
  const desktop = useDesktop();
  const { query, path } = useLocation();
  const show = SHOWS.find((s) => s.id === query.get('show'));
  const ep = query.get('ep');
  const ways = desktop ? DESKTOP_WAYS : PHONE_APPS;
  const [way, setWay] = useState(ways[0].name);
  const [vpa, setVpa] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setWay(ways[0].name), [desktop]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return <Redirect to={`/login?next=${encodeURIComponent(path + (query.toString() ? '?' + query.toString() : ''))}`} />;
  if (plus) return <Redirect to={show && ep ? `/watch/${show.id}/${ep}` : '/account'} />;

  const renewOn = date(Date.now() + PLAN.introDays * DAY);
  const needsVpa = way === 'Enter your UPI ID';

  const start = async () => {
    setError(null);
    if (needsVpa && !/^[\w.-]{2,}@[a-z]{2,}$/i.test(vpa.trim())) { setError('Enter a UPI ID like name@okbank.'); return; }
    setBusy(true);
    try {
      const { checkoutId } = await api.startSubscription(way);
      const params = new URLSearchParams({ chk: checkoutId, app: way });
      if (show && ep) { params.set('show', show.id); params.set('ep', ep); }
      navigate('/plus/approve?' + params.toString());
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className={'paywall' + (desktop ? ' paywall-desktop' : '')}>
      <div className="paywall-card">
        <div className="paywall-top">
          <button type="button" className="icon-btn" onClick={() => back('/home')} aria-label="Close"><Icon name="close" /></button>
        </div>
        <div className="paywall-hero">
          <span className="paywall-mark"><Wordmark size={44} /><span className="badge badge-plus">PLUS</span></span>
          <h1>Every show, every episode.<br />One plan.</h1>
          {show && ep && <p className="muted">Unlock Ep {ep} of “{show.title}” and everything else.</p>}
        </div>
        <ul className="perks">
          {['All premium shows and episodes', 'No ads', 'Download and watch offline (in the app)', 'New episodes every week'].map((p) => (
            <li key={p}><span className="perk-check"><Icon name="check" size={14} /></span>{p}</li>
          ))}
        </ul>
        <div className="offer">
          <span className="ribbon">First month offer</span>
          <div className="offer-row"><b>{PLAN.name}</b><span><span className="offer-price">{rupees(PLAN.introPricePaise)}</span> <span className="muted">for 30 days</span></span></div>
          <p>Then <b>{rupees(PLAN.pricePaise)}/month</b> from <b>{renewOn}</b> by UPI AutoPay. Cancel any time.</p>
        </div>
        <fieldset className="pay-ways">
          <legend>{desktop ? 'How do you want to pay?' : 'Choose your UPI app'}</legend>
          {ways.map((w) => (
            <button key={w.name} type="button" role="radio" aria-checked={way === w.name} className={'pay-way' + (way === w.name ? ' on' : '')} onClick={() => setWay(w.name)}>
              <span className="pay-badge" style={{ background: w.color } as CSSProperties}>{w.short}</span>
              <span className="pay-name">{w.name}{'hint' in w && <small>{(w as { hint: string }).hint}</small>}</span>
              <span className="radio" />
            </button>
          ))}
          {needsVpa && (
            <div className="stack-sm">
              <label htmlFor="vpa" className="label">UPI ID</label>
              <input id="vpa" className="input" placeholder="name@okbank" value={vpa} onChange={(e) => setVpa(e.target.value)} />
            </div>
          )}
        </fieldset>
        {error && <p className="field-error" role="alert">{error}</p>}
        <div className="paywall-cta">
          <button type="button" className="btn btn-lg btn-primary btn-block" disabled={busy} onClick={start}>
            <span key={busy ? 'b' : 'i'} className="btn-label">{busy ? 'Opening…' : `Pay ${rupees(PLAN.introPricePaise)} · Set up AutoPay`}</span>
          </button>
          <p className="fine">We will tell you at least 24 hours before each renewal. You can stop AutoPay any time in this app or in your UPI app. Prices include GST.</p>
        </div>
      </div>
    </div>
  );
}

export function Approve() {
  const { plan: PLAN, shows: SHOWS } = useCatalog();
  const { query } = useLocation();
  const chk = query.get('chk') || '';
  const app = query.get('app') || 'UPI app';
  const [state, setLocal] = useState<'waiting' | 'working' | 'declined'>('waiting');
  const [error, setError] = useState<string | null>(null);
  const allowed = !!getState().session && !!chk;
  const decide = async (ok: boolean) => {
    setLocal('working');
    setError(null);
    try {
      const sub = await api.confirmMandate(chk, ok);
      sub.upiApp = app.startsWith('Scan') ? 'UPI (QR)' : app.startsWith('Enter') ? 'UPI ID' : app;
      const now = Date.now();
      setState((s) => ({
        sub,
        extraNotifs: [
          { id: 'p-' + now, kind: 'payment', title: 'Welcome to Plus', body: `${rupees(PLAN.introPricePaise)} paid. Next AutoPay: ${rupees(sub.nextChargePaise)} on ${date(sub.currentPeriodEnd)}.`, at: now, link: '/account' },
          ...s.extraNotifs,
        ],
      }));
      const p = new URLSearchParams();
      if (query.get('show')) { p.set('show', query.get('show')!); p.set('ep', query.get('ep') || '1'); }
      navigate('/plus/done?' + p.toString(), { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setLocal('declined');
    }
  };

  // Live payments: Razorpay Checkout already opened the UPI app, so start waiting for the bank right away.
  const live = isLiveCheckout(chk);
  useEffect(() => { if (allowed && live && state === 'waiting') decide(true); }, [live]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!allowed) return <Redirect to="/plus" />;

  return (
    <div className="approve">
      <div className="approve-card">
        <div className="steps-line" aria-label="Payment steps">
          <span className="on">1 · Plan</span><span className="on">2 · Approve in UPI app</span><span>3 · Done</span>
        </div>
        {state === 'declined' ? (
          <>
            <h1>AutoPay was not set up</h1>
            <p className="field-error">{error}</p>
            <div className="row-wrap">
              <button type="button" className="btn btn-primary" onClick={() => navigate('/plus', { replace: true })}>Try again</button>
              <Link to="/home" className="btn btn-ghost">Go home</Link>
            </div>
          </>
        ) : (
          <>
            <span className="spinner big" aria-hidden="true" />
            <h1>Approve AutoPay in {app.startsWith('Scan') || app.startsWith('Enter') ? 'your UPI app' : app}</h1>
            <p>You will pay <b>{rupees(PLAN.introPricePaise)}</b> today. After that, up to <b>{rupees(PLAN.pricePaise)}</b> each month until you cancel.</p>
            <p className="muted small">Keep this page open. It moves on by itself once your bank confirms.</p>
            {!live && <div className="demo-box">
              <b>Preview only</b>
              <p>In the live app, your UPI app opens here. Choose what the person does:</p>
              <div className="row-wrap">
                <button type="button" className="btn btn-primary" disabled={state === 'working'} onClick={() => decide(true)}>{state === 'working' ? 'Confirming…' : 'Approve'}</button>
                <button type="button" className="btn btn-ghost" disabled={state === 'working'} onClick={() => decide(false)}>Decline</button>
              </div>
            </div>}
          </>
        )}
      </div>
    </div>
  );
}

export function PlusDone() {
  const { plan: PLAN, shows: SHOWS } = useCatalog();
  const sub = useApp((s) => s.sub);
  const { query } = useLocation();
  if (!sub) return <Redirect to="/plus" />;
  const showId = query.get('show');
  const ep = query.get('ep');
  return (
    <div className="approve">
      <div className="approve-card">
        <div className="steps-line"><span className="on">1 · Plan</span><span className="on">2 · Approve</span><span className="on">3 · Done</span></div>
        <span className="done-check"><Icon name="check" size={44} /></span>
        <h1>You are now a <em>Plus</em> member</h1>
        <p className="muted">We got your {rupees(PLAN.introPricePaise)}. UPI AutoPay is ready and all episodes are unlocked.</p>
        <dl className="kv">
          <div><dt>Plan</dt><dd>{PLAN.name}</dd></div>
          <div><dt>Paid today</dt><dd>{rupees(PLAN.introPricePaise)}</dd></div>
          <div><dt>Next AutoPay</dt><dd className="accent-text">{rupees(sub.nextChargePaise)} · {date(sub.currentPeriodEnd)}</dd></div>
          <div><dt>Paid with</dt><dd>{sub.upiApp} · {sub.vpaMasked}</dd></div>
          <div><dt>Mandate ID</dt><dd>{sub.mandateId}</dd></div>
        </dl>
        <p className="muted small">We sent your receipt by SMS and email. To stop AutoPay, go to Account › Manage AutoPay.</p>
        <div className="stack-sm">
          <Link to={showId ? `/watch/${showId}/${ep || 1}` : '/home'} className="btn btn-lg btn-primary btn-block">{showId ? `Start watching Ep ${ep}` : 'Start watching'}</Link>
          <Link to="/account" className="btn btn-lg btn-ghost btn-block">Manage AutoPay</Link>
        </div>
      </div>
    </div>
  );
}
